import React from "react";
import { COLORS, SANS, formStyles } from "./theme";
import {
  EV_TYPES,
  EV_TYPE_LABEL,
  MAX_EVIDENCE_PER_TYPE,
  MAX_FILE_SIZE_BYTES,
  MAX_PDF_PAGES,
  countEvidenceByType,
  makeEmptyEvidenceRow,
  // validateVideoUrl,   // video evidence disabled for now
} from "./evidence";
import { countPdfPages } from "./pdfPages";

export default function EvidenceList({ evidence, setEvidence, title = "Evidence", maxPerType }) {
  // Per-type count caps; defaults to the filer/disputant caps unless a role-
  // specific override (e.g. the tighter witness caps) is passed in.
  const caps = maxPerType || MAX_EVIDENCE_PER_TYPE;
  const [sizeErrors, setSizeErrors] = React.useState({});
  // const [urlErrors, setUrlErrors] = React.useState({});   // video evidence disabled for now
  const [checkingIds, setCheckingIds] = React.useState({});
  const counts = countEvidenceByType(evidence);
  const allTypesAtCap = Object.entries(counts).every(([t, n]) => n >= (caps[t] ?? Infinity));
  const imageMb = Math.round(MAX_FILE_SIZE_BYTES.image / 1024 / 1024);
  const fileMb = Math.round(MAX_FILE_SIZE_BYTES.file / 1024 / 1024);

  const setError = (id, msg) =>
    setSizeErrors((prev) => {
      const next = { ...prev };
      if (msg) next[id] = msg; else delete next[id];
      return next;
    });
  const setChecking = (id, on) =>
    setCheckingIds((prev) => {
      const next = { ...prev };
      if (on) next[id] = true; else delete next[id];
      return next;
    });
  const isPdf = (file) =>
    file && (file.type === "application/pdf" || /\.pdf$/i.test(file.name || ""));

  const addRow = () =>
    setEvidence((prev) => {
      const c = countEvidenceByType(prev);
      const nextType =
        [EV_TYPES.IMAGE, EV_TYPES.FILE /*, EV_TYPES.VIDEO */].find(
          (t) => c[t] < (caps[t] ?? Infinity)
        ) || EV_TYPES.IMAGE;
      return [...prev, { ...makeEmptyEvidenceRow(), type: nextType }];
    });

  const removeRow = (id) => {
    setEvidence((prev) => prev.filter((e) => e.id !== id));
    setSizeErrors((prev) => { const next = { ...prev }; delete next[id]; return next; });
    // setUrlErrors((prev) => { const next = { ...prev }; delete next[id]; return next; });   // video disabled
    setChecking(id, false);
  };

  // Video evidence disabled for now — URL validation handlers no longer used.
  // const handleUrlBlur = (id, url) => {
  //   const err = validateVideoUrl(url);
  //   setUrlErrors((prev) => {
  //     const next = { ...prev };
  //     if (err) next[id] = err; else delete next[id];
  //     return next;
  //   });
  // };
  //
  // const handleUrlChange = (id, url) => {
  //   changeRow(id, "url", url);
  //   if (urlErrors[id]) setUrlErrors((prev) => { const next = { ...prev }; delete next[id]; return next; });
  // };

  const changeRow = (id, field, value) =>
    setEvidence((prev) => prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)));

  const handleFileChange = async (id, itemType, file) => {
    // 1. Size check (sync) — reject oversized files outright.
    const limit = MAX_FILE_SIZE_BYTES[itemType];
    if (file && limit && file.size > limit) {
      const mb = Math.round(limit / 1024 / 1024);
      const actualMb = (file.size / 1024 / 1024).toFixed(1);
      setError(id, `“${file.name}” is ${actualMb} MB — over the ${mb} MB limit for ${EV_TYPE_LABEL[itemType]}. Not added.`);
      changeRow(id, "file", null);
      return;
    }

    if (!file) {
      setError(id, null);
      changeRow(id, "file", null);
      return;
    }

    // 2. Page check (async, PDFs only) — page count drives AI token cost and
    // hits provider page limits, so cap it. Parse failures fail OPEN (the
    // backend remains the real gate) so a quirky-but-valid PDF isn't blocked.
    if (isPdf(file)) {
      setError(id, null);
      setChecking(id, true);
      try {
        const pages = await countPdfPages(file);
        if (pages > MAX_PDF_PAGES) {
          setError(id, `“${file.name}” has ${pages} pages — over the ${MAX_PDF_PAGES}-page limit for documents. Not added.`);
          changeRow(id, "file", null);
          return;
        }
      } catch (err) {
        console.warn(`[evidence] could not read PDF page count for "${file.name}":`, err.message);
      } finally {
        setChecking(id, false);
      }
    }

    // 3. Accepted.
    setError(id, null);
    changeRow(id, "file", file);
  };

  return (
    <div style={formStyles.fieldGroup}>
      <h2 style={formStyles.sectionTitle}>
        {title}{" "}
        <span style={{ fontSize: 11, fontWeight: 400, textTransform: "none", letterSpacing: 0, opacity: 0.7 }}>
          (max {caps.image} images, {caps.file} files)
        </span>
      </h2>

      <p style={styles.helpText}>
        Accepted: <strong>images</strong> (JPG, PNG, GIF, WebP — max {imageMb} MB each) and{" "}
        <strong>documents</strong> (PDF — max {fileMb} MB and {MAX_PDF_PAGES} pages each; or{" "}
        <strong>.txt / .html / .json</strong> text files — max {fileMb} MB each). The AI panel (Claude,
        GPT-4o, Gemini) reads PDFs, images, and those text files directly, so upload official documents
        as <strong>PDF</strong> — Word (.doc/.docx) and other formats may not be read by the judges.
      </p>

      {evidence.map((item) => (
        <div key={item.id} style={styles.rowWrap}>
          <div style={styles.row}>
            <select
              style={styles.typeSelect}
              value={item.type}
              onChange={(e) => changeRow(item.id, "type", e.target.value)}
            >
              <option
                value={EV_TYPES.IMAGE}
                disabled={item.type !== EV_TYPES.IMAGE && counts.image >= caps.image}
              >
                Image
              </option>
              <option
                value={EV_TYPES.FILE}
                disabled={item.type !== EV_TYPES.FILE && counts.file >= caps.file}
              >
                File
              </option>
              {/* Video evidence disabled for now.
              <option
                value={EV_TYPES.VIDEO}
                disabled={item.type !== EV_TYPES.VIDEO && counts.video >= MAX_EVIDENCE_PER_TYPE.video}
              >
                Video URL
              </option>
              */}
            </select>

            {/* Video evidence disabled for now — always a file input.
            {item.type === EV_TYPES.VIDEO ? (
              <input
                type="text"
                placeholder="https://youtube.com/... or vimeo.com/..."
                style={styles.fileInput}
                value={item.url}
                onChange={(e) => handleUrlChange(item.id, e.target.value)}
                onBlur={(e) => handleUrlBlur(item.id, e.target.value)}
              />
            ) : ( ... )} */}
            <input
              type="file"
              accept={
                item.type === EV_TYPES.IMAGE
                  ? "image/*"
                  : ".pdf,application/pdf,.txt,.html,.json,text/plain,text/html,application/json"
              }
              style={styles.fileInput}
              onChange={(e) => handleFileChange(item.id, item.type, e.target.files[0])}
            />

            {evidence.length > 1 && (
              <button
                type="button"
                style={styles.removeBtn}
                onClick={() => removeRow(item.id)}
                title="Remove evidence"
              >
                ×
              </button>
            )}
          </div>
          {checkingIds[item.id] && (
            <p style={styles.checking}>Checking PDF…</p>
          )}
          {sizeErrors[item.id] && (
            <p style={styles.sizeError}>{sizeErrors[item.id]}</p>
          )}
          <textarea
            style={styles.infoInput}
            placeholder="Please briefly explain how this evidence is related to the dispute. (required)"
            value={item.info || ""}
            onChange={(e) => changeRow(item.id, "info", e.target.value)}
            maxLength={500}
            rows={2}
            required
          />
          {/* Video evidence disabled for now.
          {urlErrors[item.id] && (
            <p style={styles.sizeError}>{urlErrors[item.id]}</p>
          )} */}
        </div>
      ))}

      <button
        type="button"
        style={{
          ...styles.addBtn,
          opacity: allTypesAtCap ? 0.5 : 1,
          cursor: allTypesAtCap ? "not-allowed" : "pointer",
        }}
        onClick={addRow}
        disabled={allTypesAtCap}
        title={allTypesAtCap ? "Evidence limit reached" : ""}
      >
        + Add Evidence
      </button>
    </div>
  );
}

const styles = {
  rowWrap: { marginBottom: 8 },
  row: { display: "flex", alignItems: "center", gap: 8 },
  helpText: { margin: "0 0 14px 0", fontSize: 12, color: COLORS.inkSoft, lineHeight: 1.55, fontFamily: SANS },
  checking: { margin: "2px 0 0 0", fontSize: 11, color: COLORS.inkMuted, fontFamily: SANS },
  sizeError: { margin: "2px 0 0 0", fontSize: 11, color: "#cc3333", fontFamily: SANS },
  typeSelect: {
    fontSize: 12,
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    padding: "6px 8px",
    fontFamily: SANS,
    color: COLORS.inkSoft,
  },
  fileInput: {
    flex: 1,
    fontSize: 12,
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    padding: "6px 10px",
    fontFamily: SANS,
    color: COLORS.inkSoft,
  },
  infoInput: {
    width: "100%",
    marginTop: 8,
    fontSize: 12,
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    padding: "6px 10px",
    fontFamily: SANS,
    color: COLORS.inkSoft,
    resize: "vertical",
    boxSizing: "border-box",
  },
  removeBtn: {
    background: "#ff4d4d18",
    border: "1px solid #ff4d4d",
    color: "#cc3333",
    borderRadius: 4,
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: 16,
    padding: 0,
  },
  addBtn: {
    background: "transparent",
    border: `1px dashed ${COLORS.borderStrong}`,
    color: COLORS.inkSoft,
    borderRadius: 6,
    padding: "8px 14px",
    fontSize: 12,
    width: "100%",
    fontFamily: SANS,
  },
};
